import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { ApiKey } from "@models/integrations";
import { PaymentMethod } from "@models/payment";
import { Role } from "@models/role";
import { Integrations } from "@models/settings";
import { User, UserStatus, Workspace } from "@models/user";
import { Wallet, WalletLog } from "@models/wallet";
import { map, Observable } from "rxjs";

import { CreateApiKeyDto, CreatePaymentMethodDto, UpdateUserDto } from "../request-dtos/user.dto";
import { BrandingConfig } from "./branding.service";
import { HttpService } from "./http.service";
import { SessionService } from "./session.service";

@Injectable({
    providedIn:"root"
})
export class UserService {
    constructor(private http: HttpService,
        private sessionSvc: SessionService
    ) {}

    selectWorkspace(workspaceUid: string): Observable<{selectedWorkspace: Workspace, role: Role, branding: BrandingConfig}> {
        return this.http.patch(`${environment.apiUrl}/${workspaceUid}/workspace-user/selectUserWorkspace`,{}).pipe(
            map((res: any) => {
                let workspaceRole: {selectedWorkspace: Workspace, role: Role, branding: BrandingConfig} = res.data;
                return workspaceRole;
            })
        );
    }

    /**
     * Update user information
     */
    updateUserInfo(updateUserDto: UpdateUserDto): Observable<any> {
        return this.http.patch(`${environment.apiUrl}/user/updateInfo`, updateUserDto).pipe(
            map((res: any) => res.data)
        );
    }

    /** Whether the signed-in user has a local password (false for typical Google OAuth until set). */
    getPasswordStatus(): Observable<{ hasPasswordConfigured: boolean }> {
        return this.http.get(`${environment.apiUrl}/user/password-status`).pipe(
            map((res: any) => res?.data ?? res),
        );
    }

    /**
     * Create API key
     */
    createApiKey(createApiKeyDto: CreateApiKeyDto): Observable<ApiKey> {
        return this.http.post(`${environment.apiUrl}/user/add-apiKey`, createApiKeyDto).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get current API keys
     */
    getCurrentApiKeys(): Observable<ApiKey[]> {
        return this.http.get(`${environment.apiUrl}/user/get-apiKeys`).pipe(
            map((res: any) => res.data || [])
        );
    }

    /**
     * Remove API key by ID
     */
    removeApiKey(id: number): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/user/${id}/delete-apiKey`).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Update API key by ID
     */
    updateApiKey(id: number, apiKey: CreateApiKeyDto): Observable<ApiKey> {
        return this.http.patch(`${environment.apiUrl}/user/${id}/update-apiKey`, apiKey).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Find API key by ID
     */
    findApiKeyById(id: number): Observable<ApiKey> {
        return this.http.get(`${environment.apiUrl}/user/${id}/get-apiKey`).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get agency user wallet
     */
    getAgencyUserWallet(): Observable<Wallet> {
        return this.http.get(`${environment.apiUrl}/user/wallets`).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get wallet logs for user wallet
     */
    getWalletLogsForUserWallet(): Observable<WalletLog[]> {
        return this.http.get(`${environment.apiUrl}/user/wallet-logs`).pipe(
            map((res: any) => res.data || [])
        );
    }

    /**
     * Add payment method
     */
    addPaymentMethod(dto: CreatePaymentMethodDto): Observable<PaymentMethod> {
        return this.http.post(`${environment.apiUrl}/user/add-payment-method`, dto).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get payment methods
     */
    getPaymentMethods(): Observable<PaymentMethod[]> {
        return this.http.get(`${environment.apiUrl}/user/payment-methods`).pipe(
            map((res: any) => res.data || [])
        );
    }

    /**
     * Get OAuth link for Stripe connection
     */
    getOAuthLink(): string {
        return `${environment.apiUrl}/stripe/connect`;
    }

    /**
     * Connect Stripe account via OAuth
     */
    connectStripe(code: string): Observable<any> {
        return this.http.get(`${environment.apiUrl}/user/stripe/oauth/callback`, { code }).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Disconnect Stripe account
     */
    disconnectIntegration(integrationId: number): Observable<any> {
        return this.http.delete(`${environment.apiUrl}/user/${integrationId}/delete-integration`).pipe(
            map((res: any) => res.data)
        );
    }

    getIntegrations(): Observable<Integrations[]> {
        return this.http.get(`${environment.apiUrl}/user/integrations`).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get Stripe connection status
     */
    getStripeConnectionStatus(): Observable<any> {
        return this.http.get(`${environment.apiUrl}/user/stripe/status`).pipe(
            map((res: any) => res.data)
        );
    }

    /**
     * Get workspaces and member information
     */
    getWorkspacesAndMember(): Observable<any> {
        return this.http.get(`${environment.apiUrl}/user/workspaces-and-user`).pipe(
            map((res: any) => res.data)
        );
    }


    allocateCredits(amount: number,workspaceUid: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}/user/${workspaceUid}/transfer-credits/${amount}`,{}).pipe(
            map((res: any) => res.data)
        );
    }


    updateServiceCost(serviceCost: number): Observable<any> {
        return this.http.patch(`${environment.apiUrl}/user/service-cost`, { serviceCost }).pipe(
            map((res: any) => res.data)
        );
    }

    updatePresence(status: UserStatus): Observable<User> {
        return this.http.patch(`${environment.apiUrl}/user/presence`, { status }).pipe(
            map((res: any) => res.data as User)
        );
    }

    /**
     * Export all user data (GDPR Right to Data Portability)
     */
    exportUserData(): Observable<any> {
        return this.http.get(`${environment.apiUrl}/user/export-data`).pipe(
            map((res: any) => res.data || res)
        );
    }

    /**
     * Delete all user data (GDPR Right to be Forgotten)
     * WARNING: This is irreversible!
     */
    deleteUserData(): Observable<{ message: string; deletedCount: number }> {
        return this.http.delete(`${environment.apiUrl}/user/delete-all-data`).pipe(
            map((res: any) => res.data || res)
        );
    }

    currentUser(): Observable<User> {
        return this.http.get(`${environment.apiUrl}/user/currentUser`).pipe(
            map((res: any) => res.data)
        );
    }
}